import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Code, Copy, Check } from 'lucide-react';

interface PromptDebuggerProps {
  title: string;
  prompt: string;
  response?: string;
  metadata?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
    processingTime?: number;
    confidence?: number;
  };
  className?: string;
}

const PromptDebugger: React.FC<PromptDebuggerProps> = ({
  title,
  prompt,
  response,
  metadata,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const copyToClipboard = async (text: string, type: 'prompt' | 'response') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'prompt') {
        setCopiedPrompt(true);
        setTimeout(() => setCopiedPrompt(false), 2000);
      } else {
        setCopiedResponse(true);
        setTimeout(() => setCopiedResponse(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <div className={`border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">{title}</span>
          {metadata?.confidence && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              {Math.round(metadata.confidence * 100)}% confidence
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          {/* Metadata */}
          {metadata && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              {metadata.model && (
                <div>
                  <span className="text-gray-500">Model:</span>
                  <span className="ml-1 font-medium">{metadata.model}</span>
                </div>
              )}
              {metadata.temperature !== undefined && (
                <div>
                  <span className="text-gray-500">Temperature:</span>
                  <span className="ml-1 font-medium">{metadata.temperature}</span>
                </div>
              )}
              {metadata.maxTokens && (
                <div>
                  <span className="text-gray-500">Max Tokens:</span>
                  <span className="ml-1 font-medium">{metadata.maxTokens}</span>
                </div>
              )}
              {metadata.processingTime && (
                <div>
                  <span className="text-gray-500">Processing:</span>
                  <span className="ml-1 font-medium">{(metadata.processingTime / 1000).toFixed(2)}s</span>
                </div>
              )}
            </div>
          )}

          {/* Prompt Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-800">Input Prompt</h4>
              <button
                onClick={() => copyToClipboard(prompt, 'prompt')}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                {copiedPrompt ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <div className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs font-mono overflow-x-auto max-h-60 overflow-y-auto">
              <pre className="whitespace-pre-wrap">{prompt}</pre>
            </div>
          </div>

          {/* Response Section */}
          {response && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-800">AI Response</h4>
                <button
                  onClick={() => copyToClipboard(response, 'response')}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                >
                  {copiedResponse ? (
                    <>
                      <Check className="w-3 h-3" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="bg-blue-900 text-blue-100 p-3 rounded-md text-xs font-mono overflow-x-auto max-h-60 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{response}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptDebugger;